import React, { useCallback, useState } from 'react';
import { Platform, RefreshControl } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import PortalsList from './PortalsList';
import useProfileConnectedHealthSystems from '../../../hooks/api/useProfileConnectedHealthSystems';
import useFastenConnections from '../../../hooks/api/useFastenConnections';
import { useQueryClient } from '@tanstack/react-query';
import usePendingFastenConnections from '../../../hooks/api/usePendingFastenConnections';
import NoConnectedSystemsGate from './NoConnectedSystemsGate';
import { ActionButtonLayout } from '../../v2BaseComponents/ActionButtonLayout';
import { getIntl } from '../../../utils/intl';
import { useProfilesTabNavigation } from '../../../utils/appNavigationNames';
import useRecordEvent from '../../../hooks/useRecordEvent';
import MainContentContainer from '../../v2BaseComponents/MainContentContainer';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import RefreshProgress from '../RefreshProgress';
import { useHasPermission } from '../../v2BaseComponents/Permissions/hooks';
import {
    useHasReachedPortalConnectLimit,
    useMedicalProfileUsageMetric,
} from '../../../hooks/api/useMedicalProfileUsageMetric';
import Alert from '../../v2BaseComponents/Alert';
import { usePortalConnectUsesFastenFlow } from '../../../hooks/api/usePortalConnectUsesFastenFlow';
import useFastenSandboxMode from '../../../hooks/useFastenSandboxMode';

const intl = getIntl('profile-connect-tab', {
    addPortal: 'Connect with 1up Health',
    addFastenPortal: 'Connect with Fasten',
    connectViaTefca: 'Connect via TEFCA',
    refreshLimitReached: 'Monthly Refresh Limit Reached',
    fastenSuccessTitle: 'Connection Successful',
    fastenSuccessMessage:
        'Your healthcare provider has been connected. It may take up to a minute for your records to appear.',
    refresh: 'Refresh',
    importingTitle: 'Connecting your portal',
    importingMessage: 'Importing your health records — this can take up to a minute.',
});

export type ProfileConnectContentOwnProps = {
    medicalProfileId: string;
    forceRefresh?: boolean;
    forceFastenRefresh?: boolean;
    showFastenPendingAlert?: boolean;
};

export type ProfileConnectContentProps = ProfileConnectContentOwnProps;

const ProfileConnectContent: React.FC<ProfileConnectContentProps> = (props) => {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isImportingNewConnection, setIsImportingNewConnection] = useState(false);
    const hasReachedPortalConnectionLimit = useHasReachedPortalConnectLimit(props.medicalProfileId);
    const { hasReachedLimit: hasReachedFastenRefreshLimit } = useMedicalProfileUsageMetric({
        medicalProfileId: props.medicalProfileId,
        metric: 'fasten-refresh',
    });
    const navigation = useProfilesTabNavigation();
    const hasViewPermission = useHasPermission(props.medicalProfileId, 'patientPortals', 'view');
    const hasPermission = useHasPermission(props.medicalProfileId, 'patientPortals', 'edit');
    const recordEvent = useRecordEvent();
    const { showFastenConnectUi } = usePortalConnectUsesFastenFlow(props.medicalProfileId);
    const { isSandboxMode } = useFastenSandboxMode();
    const {
        isLoading: isLoadingConnectedHealthSystems,
        connectedHealthSystems,
        pendingDataHealthSystems,
    } = useProfileConnectedHealthSystems({
        medicalProfileId: props.medicalProfileId,
    });

    const {
        isLoading: isLoadingFastenConnections,
        connections: fastenConnections,
        refetch: refetchFastenConnections,
    } = useFastenConnections({
        medicalProfileId: props.medicalProfileId,
        enabled: hasViewPermission,
    });

    // Invalidate connections cache when screen gains focus (e.g. after reconnect/new connection)
    const queryClient = useQueryClient();
    useFocusEffect(
        React.useCallback(() => {
            if (hasViewPermission) {
                queryClient.invalidateQueries({ queryKey: ['fasten-connections', props.medicalProfileId] });
            }
        }, [queryClient, props.medicalProfileId, hasViewPermission]),
    );

    const { pendingConnections: pendingFastenConnections } = usePendingFastenConnections({
        medicalProfileId: props.medicalProfileId,
        enabled: hasViewPermission,
    });

    const onRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            await refetchFastenConnections();
        } finally {
            setIsRefreshing(false);
        }
    }, [refetchFastenConnections]);

    // After returning from the Fasten OAuth (forceFastenRefresh), the new connection is created by a
    // server webhook a beat later (usually within a minute, but it can take longer). A single refetch on
    // focus misses it, leaving the user to pull-to-refresh with no feedback. Show an importing banner and
    // poll for a window so the connection surfaces on its own and the user has feedback the whole time.
    React.useEffect(() => {
        if (!props.forceFastenRefresh || !hasViewPermission || !showFastenConnectUi) return;

        setIsImportingNewConnection(true);
        let elapsedMs = 0;
        const intervalMs = 4000;
        const maxMs = 120000;
        const interval = setInterval(() => {
            elapsedMs += intervalMs;
            refetchFastenConnections();
            if (elapsedMs >= maxMs) {
                clearInterval(interval);
                setIsImportingNewConnection(false);
            }
        }, intervalMs);

        return () => clearInterval(interval);
    }, [props.forceFastenRefresh, hasViewPermission, showFastenConnectUi, refetchFastenConnections]);

    // Once the new connection lands (it arrives in a 'refreshing' import state), hand off to the
    // connection card's own "Importing" status and hide the banner.
    React.useEffect(() => {
        if (fastenConnections.some((c) => c.connectionStatus === 'refreshing')) {
            setIsImportingNewConnection(false);
        }
    }, [fastenConnections]);

    const isLoading = isLoadingConnectedHealthSystems || isLoadingFastenConnections;

    const showList =
        connectedHealthSystems?.length ||
        pendingDataHealthSystems?.length ||
        fastenConnections?.length ||
        pendingFastenConnections?.length ||
        props.forceRefresh ||
        props.forceFastenRefresh;

    const actions = showFastenConnectUi
        ? [
              {
                  onPress: () => {
                      navigation.navigate('add-fasten-portal', {
                          profileId: props.medicalProfileId,
                      });
                      recordEvent('searchFastenPortals');
                  },
                  title: hasReachedFastenRefreshLimit ? intl('refreshLimitReached') : intl('addFastenPortal'),
                  icon: 'HorizontalArrows2' as const,
                  testID: 'prompt-fasten-portal-button',
                  show: hasPermission,
                  isDisabled: hasReachedFastenRefreshLimit,
              },
              {
                  onPress: () => {
                      navigation.navigate('add-tefca-connection', {
                          profileId: props.medicalProfileId,
                      });
                      recordEvent('connectViaTefca');
                  },
                  title: hasReachedFastenRefreshLimit ? intl('refreshLimitReached') : intl('connectViaTefca'),
                  icon: 'Share2' as const,
                  testID: 'prompt-tefca-connect-button',
                  // A development trial: only staff with the sandbox toggle on, and only on the web app.
                  show: hasPermission && isSandboxMode && Platform.OS === 'web',
                  isDisabled: hasReachedFastenRefreshLimit,
              },
          ]
        : [
              {
                  onPress: () => {
                      navigation.navigate('add-portal', {
                          profileId: props.medicalProfileId,
                      });
                      recordEvent('searchPatientPortals');
                  },
                  title: intl('addPortal'),
                  icon: 'HorizontalArrows2' as const,
                  testID: 'prompt-portal-search-button',
                  show: hasPermission,
                  isDisabled: hasReachedPortalConnectionLimit,
              },
          ];

    return (
        <ActionButtonLayout
            scrollable
            actions={actions}
            refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} />}
        >
            <AnimateLoadedContent isLoading={isLoading}>
                {() => (
                    <MainContentContainer>
                        {props.showFastenPendingAlert && (
                            <Alert
                                type="success"
                                title={intl('fastenSuccessTitle')}
                                message={intl('fastenSuccessMessage')}
                                isDismissible
                                persistedDismissKey={`fasten-pending-${props.medicalProfileId}`}
                                style={{ marginBottom: 16 }}
                                actionItems={[
                                    {
                                        title: intl('refresh'),
                                        onPress: () => refetchFastenConnections(),
                                    },
                                ]}
                            />
                        )}
                        {isImportingNewConnection && (
                            <Alert
                                type="info"
                                title={intl('importingTitle')}
                                message={intl('importingMessage')}
                                style={{ marginBottom: 16 }}
                            />
                        )}
                        <RefreshProgress medicalProfileId={props.medicalProfileId} style={{ marginBottom: 8 }} />
                        {showList ? (
                            <PortalsList
                                medicalProfileId={props.medicalProfileId}
                                isAForceRefresh={props.forceRefresh}
                            />
                        ) : (
                            <NoConnectedSystemsGate medicalProfileId={props.medicalProfileId} canAdd={hasPermission} />
                        )}
                    </MainContentContainer>
                )}
            </AnimateLoadedContent>
        </ActionButtonLayout>
    );
};

export default React.memo(ProfileConnectContent);
